import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled,
  IconButton,
  Collapse,
  Typography,
  Link,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { formattedAmountWithoutAed } from "../../../utils/CommonFunction";
import { ReactComponent as ProjectActiveIcon } from "../../../assets/svg/ProjectActiveIcon.svg";
import moment from "moment";
import { statusStyleArray } from "../config";
import { ReactComponent as ArrowIcon } from "../../../assets/svg/ArrowIcon.svg";
import { ReactComponent as PdfIcon } from "../../../assets/svg/SmallPdfIcon.svg";
import { useNavigate } from "react-router-dom";
import { ReactComponent as SmallDhiramIcon } from "../../../assets/svg/SmallDhiramIcon.svg";
import { renderCurrencyIcon } from "../../../utils/CurrencyUtils";
import { toast } from "react-toastify";
import { FiUpload } from "react-icons/fi";
import { DeleteOutline } from "@mui/icons-material";
import { BsFiletypePdf } from "react-icons/bs";
import { getApiData } from "../../../utils/APIHelper";

export const getStatusDisplayText = (text) => {
  if (typeof text !== "string") return text;
  
  if (text.toLowerCase() === "created") {
    return "-";
  }
  
  const displayText = text
    .replace(/[-_]/g, " ")
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  return displayText;
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: "none",
  padding: "1vh 1vw !important",
}));

const StyledTableCellWithoutPadding = styled(TableCell)(({ theme }) => ({
  borderBottom: "none",
  padding: "0 !important",
}));

export default function BalanceDetails({ details, fetchData }) {
  const navigate = useNavigate();
  const [expandedRows, setExpandedRows] = useState({});

  const handleExpandClick = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const MAX_FILE_SIZE_MB = 50;

  const handleFileSelect = async (e, payoutId) => {
    const file = e.target.files[0];

    if (!file) {
      toast.error("No file selected");
      return;
    }

    if (file.type !== "application/pdf") {
      toast.error("Only PDF files are allowed");
      return;
    }

    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > MAX_FILE_SIZE_MB) {
      toast.error("File size should not exceed 50MB");
      return;
    }

    await handleStep1(file, payoutId);
  };

  const handleStep1 = async (file, payoutId) => {
    try {
      const toastId = toast.loading("Preparing upload...");

      const response = await getApiData(
        `api/v2/blob/sas/upload`,
        "POST",
        {
          entityType: "contractorPayout",
          entityId: payoutId,
          fileName: file.name,
        },
        { "Content-Type": "application/json" },
        true
      );

      if (response) {
        toast.update(toastId, {
          render: "Upload link generated",
          type: "success",
          isLoading: false,
          autoClose: 1000,
        });
        await uploadToBlob(file, response);
      } else {
        toast.update(toastId, {
          render: "Failed to get upload URL",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.update("prepareUpload", {
        render: "Error generating upload link",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
      console.error(error);
    }
  };

  const uploadToBlob = async (file, sasUrl) => {
    const toastId = toast.loading("Uploading PDF...");
    try {
      const url = new URL(sasUrl);
      const fileId = url.searchParams.get("id");

      await fetch(sasUrl, {
        method: "PUT",
        headers: {
          "x-ms-blob-type": "BlockBlob",
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });

      toast.update(toastId, {
        render: "PDF uploaded to blob",
        type: "success",
        isLoading: false,
        autoClose: 1000,
      });

      await sendFileUrlToServer(fileId);
    } catch (error) {
      toast.update(toastId, {
        render: "Upload to blob failed",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
      console.error(error);
    }
  };

  const sendFileUrlToServer = async (fileId) => {
    const toastId = toast.loading("Saving invoice...");
    try {
      const result = await getApiData(
        `api/v2/blob/confirm/upload/${fileId}`,
        "PUT",
        {},
        { "Content-Type": "application/json" },
        true
      );

      if (result) {
        toast.update(toastId, {
          render: "PDF uploaded successfully",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
        fetchData();
      } else {
        toast.update(toastId, {
          render: "Failed to save invoice",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.update(toastId, {
        render: "Error saving invoice",
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
      console.error(error);
    }
  };

  const handleDeleteFile = async (fileId) => {
    try {
      const toastId = toast.loading("Deleting file...");

      const response = await getApiData(
        `api/v2/blob/${fileId}`,
        "DELETE",
        {},
        { "Content-Type": "application/json" },
        true
      );

      if (response) {
        toast.update(toastId, {
          render: "File deleted successfully",
          type: "success",
          isLoading: false,
          autoClose: 3000,
        });
        fetchData();
      } else {
        toast.update(toastId, {
          render: "Failed to delete file",
          type: "error",
          isLoading: false,
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error("Error deleting file");
      console.error(error);
    }
  };
  return (
    <TableContainer
      component={Paper}
      sx={{
        width: "100%",
        overflow: "auto !important",
        boxShadow: "none",
        maxHeight: "77vh",
      }}
    >
      <Table
        sx={{
          "& thead": {
            "& th": {
              color: "#949DAA",
              fontWeight: "500",
            },
          },
          "& tbody": {
            "& tr:hover": {
              backgroundColor: "#f7f7f7",
            },
            "& tr": {
              borderBottom: "none",
            },
          },
        }}
      >
        <TableHead>
          <TableRow>
            <StyledTableCell>Project Name</StyledTableCell>

            <StyledTableCell sx={{ flex: 0.5, textAlign: "right" }}>
              <strong
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                Total Project Value ({renderCurrencyIcon('small')})
              </strong>
            </StyledTableCell>

            <StyledTableCell sx={{ flex: 0.5, textAlign: "right" }}>
              <strong
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                Total Paid Amount ({renderCurrencyIcon('small')})
              </strong>
            </StyledTableCell>

            <StyledTableCell sx={{ flex: 0.5, textAlign: "right" }}>
              <strong
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                Total Due Amount ({renderCurrencyIcon('small')})
              </strong>
            </StyledTableCell>

            <StyledTableCell sx={{ flex: 0.5, textAlign: "right" }}>
              <strong
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                Total Not Due Amount ({renderCurrencyIcon('small')})
              </strong>
            </StyledTableCell>

            <StyledTableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {details?.map((item) => (
            <React.Fragment key={item.id}>
              <TableRow>
                <StyledTableCell>
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      gap: "7px",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      navigate(`/project/details/${item?.proposalId}`)
                    }
                  >
                    <ProjectActiveIcon />
                    {item?.name}
                  </Box>
                </StyledTableCell>
                <StyledTableCell sx={{ flex: 0.5, textAlign: "right" }}>
                  {formattedAmountWithoutAed(item?.totalCost)}
                </StyledTableCell>
                <StyledTableCell sx={{ flex: 0.5, textAlign: "right" }}>
                  {formattedAmountWithoutAed(item?.totalPaidAmount)}
                </StyledTableCell>
                <StyledTableCell sx={{ flex: 0.5, textAlign: "right" }}>
                  {formattedAmountWithoutAed(item?.totalDueAmount)}
                </StyledTableCell>
                <StyledTableCell sx={{ flex: 0.5, textAlign: "right" }}>
                  {formattedAmountWithoutAed(item?.totalNotDueAmount)}
                </StyledTableCell>
                <StyledTableCell sx={{ textAlign: "right" }}>
                  <IconButton
                    onClick={() => handleExpandClick(item.id)}
                    size="small"
                  >
                    {expandedRows[item.id] ? (
                      <ExpandLessIcon />
                    ) : (
                      <ExpandMoreIcon />
                    )}
                  </IconButton>
                </StyledTableCell>
              </TableRow>

              {expandedRows[item.id] && (
                <TableRow>
                  <StyledTableCellWithoutPadding
                    colSpan={6}
                    sx={{
                      paddingBottom: 0,
                      paddingTop: 0,
                      borderBottom: "none",
                      paddingLeft: "1.5rem !important",
                    }}
                  >
                    <Collapse
                      in={expandedRows[item.id] || false}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                        }}
                      >
                        <ArrowIcon />
                        <Table
                          size="small"
                          sx={{ width: "100%", marginTop: "1.5vh" }}
                        >
                          <TableHead>
                            <TableRow>
                              <StyledTableCell>
                                Payment Group Name
                              </StyledTableCell>
                              <StyledTableCell>Status</StyledTableCell>
                              <StyledTableCell>Due On</StyledTableCell>
                              <StyledTableCell>Settled On</StyledTableCell>
                              <StyledTableCell>Mode</StyledTableCell>
                              <StyledTableCell sx={{ textAlign: "right" }}>
                                <strong
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  Payment Group Amount ({renderCurrencyIcon('small')})
                                </strong>
                              </StyledTableCell>
                              <StyledTableCell>Paymnet Receipt</StyledTableCell>
                              <StyledTableCell>Invoice</StyledTableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {/* {item.payouts?.map((payout) => ( */}
                            {item?.payouts?.map((payout) => {
                              const metaData = payout?.metadata
                                ? JSON.parse(payout?.metadata)
                                : null;

                              const invoiceData = payout?.invoice
                                ? payout.invoice
                                : payout?.invoiceUrl
                                ? JSON.parse(payout.invoiceUrl)
                                : null;

                              let invoiceStatus = null;
                              if (invoiceData) {
                                if (invoiceData.status) {
                                  invoiceStatus = invoiceData.status;
                                } else {
                                  invoiceStatus = invoiceData.approve
                                    ? "APPROVED"
                                    : "IN_REVIEW";
                                }
                              }

                              const fileUrl = metaData?.file?.fileUrl;
                              return (
                                <TableRow key={payout.groupName}>
                                  <StyledTableCell>
                                    {payout.groupName}
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    {payout?.status !== "CREATED" && (
                                      // <Typography
                                      //   style={{
                                      //     borderRadius: "34px",
                                      //     padding: "4px 8px",
                                      //     fontSize: "14px",
                                      //     minWidth: "7ch",
                                      //     width: "fit-content",
                                      //     backgroundColor:
                                      //       statusStyleArray.find(
                                      //         (style) =>
                                      //           style.status === payout?.status
                                      //       )?.backgroundColor || "transparent",
                                      //     color:
                                      //       statusStyleArray.find(
                                      //         (style) =>
                                      //           style.status === payout?.status
                                      //       )?.color || "black",
                                      //     border: "none",
                                      //     textAlign: "center",
                                      //   }}
                                      // >
                                      //   {getStatusDisplayText(payout?.status)}
                                      // </Typography>
                                      <div
                                        style={{
                                          display: "flex",
                                          flexDirection: "row",
                                          alignItems: "center",
                                        }}
                                      >
                                        <Box
                                          style={{
                                            width: "15px",
                                            height: "15px",
                                            borderRadius: "50%",
                                            backgroundColor:
                                              statusStyleArray.find(
                                                (style) =>
                                                  style.status ===
                                                  payout?.status
                                              )?.backgroundColor ||
                                              "transparent",
                                            border: "3px solid #e5e5e5",
                                          }}
                                        />
                                        <Typography
                                          style={{
                                            fontSize: "14px",
                                            marginLeft: "8px",
                                            color: "#0D0D0E",
                                          }}
                                        >
                                          {getStatusDisplayText(payout?.status)}
                                        </Typography>
                                      </div>
                                    )}
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    {payout?.dueDate &&
                                      moment(payout?.dueDate).format(
                                        "MMM DD, YYYY"
                                      )}
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    {payout?.releaseDate &&
                                      moment(payout?.releaseDate).format(
                                        "MMM DD, YYYY"
                                      )}
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    {payout.settlementType || "-"}
                                  </StyledTableCell>
                                  <StyledTableCell sx={{ textAlign: "right" }}>
                                    {formattedAmountWithoutAed(payout.amount)}
                                  </StyledTableCell>{" "}
                                  <StyledTableCell>
                                    {fileUrl && (
                                      <Link
                                        href={fileUrl}
                                        target="_blank"
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          textDecoration: "none",
                                        }}
                                      >
                                        <PdfIcon />
                                      </Link>
                                    )}
                                  </StyledTableCell>
                                  <StyledTableCell>
                                    {payout.amount !== 0 && (
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "10px",
                                        }}
                                      >
                                        {!invoiceData?.url ? (
                                          payout.status === "DUE" && payout.groupName  && (
                                            <Button
                                              variant="contained"
                                              component="label"
                                              sx={{
                                                backgroundColor: "#4D6BF6",
                                                borderRadius: "12px",
                                                fontSize: "14px",
                                                fontWeight: 500,
                                                boxShadow: "none",
                                                color: "#fff",
                                                border: "none",
                                                padding: ".7vh 1vw",
                                                cursor: "pointer",
                                                fontFamily: "Inter Tight",
                                                whiteSpace: "nowrap",
                                                "&:hover": {
                                                  backgroundColor: "#4D6BF6",
                                                  border: "none",
                                                },
                                              }}
                                            >
                                              Upload Invoice
                                              <input
                                                id={`pdf-upload-${payout.id}`}
                                                type="file"
                                                accept="application/pdf"
                                                style={{ display: "none" }}
                                                onChange={(e) =>
                                                  handleFileSelect(e, payout.id)
                                                }
                                              />
                                            </Button>
                                          )
                                        ) : (
                                          <>
                                            <Link
                                              href={invoiceData.url}
                                              target="_blank"
                                              sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                textDecoration: "none",
                                              }}
                                              title="Download Invoice"
                                            >
                                              <PdfIcon />
                                            </Link>

                                            {invoiceStatus === "IN_REVIEW" && (
                                              <Box
                                                sx={{
                                                  color: "#E6A23C",
                                                  fontWeight: 500,
                                                  fontSize: "14px",
                                                }}
                                              >
                                                In Review
                                              </Box>
                                            )}

                                            {invoiceStatus === "APPROVED" && (
                                              <Box
                                                sx={{
                                                  color: "#28a745",
                                                  fontWeight: 500,
                                                  fontSize: "14px",
                                                }}
                                              >
                                                Approved
                                              </Box>
                                            )}

                                            {invoiceStatus === "REJECTED" && (
                                              <>
                                                <Box
                                                  sx={{
                                                    color: "#D64B64",
                                                    fontWeight: 500,
                                                    fontSize: "14px",
                                                  }}
                                                >
                                                  Rejected
                                                </Box>
                                                <Button
                                                  variant="outlined"
                                                  onClick={() =>
                                                    handleDeleteFile(
                                                      invoiceData.blobFileId,
                                                      item.id
                                                    )
                                                  }
                                                  sx={{
                                                    backgroundColor: "#fff",
                                                    borderRadius: "8px",
                                                    fontSize: "14px",
                                                    fontWeight: 500,
                                                    boxShadow: "none",
                                                    color: "#D64B64",
                                                    border: "1px solid #D7D8D9",
                                                    padding: ".5vh .8vw",
                                                    cursor: "pointer",
                                                    fontFamily: "Inter Tight",
                                                    whiteSpace: "nowrap",
                                                    minWidth: 0,
                                                    "&:hover": {
                                                      backgroundColor: "#fff",
                                                      border:
                                                        "1px solid #D7D8D9",
                                                    },
                                                  }}
                                                >
                                                  Delete
                                                </Button>
                                              </>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    )}
                                  </StyledTableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </StyledTableCellWithoutPadding>
                </TableRow>
              )}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
